import { formatPhone, formatDid } from "@/lib/phone";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { firestore } from "@/integrations/firebase/client";
import { queryDocs } from "@/integrations/firebase/data";
import { recordingUrl } from "@/integrations/firebase/recording";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/hooks/use-auth";
import { formatDuration, formatDateTime, formatWhen, callStatusLabel, isReallyLive } from "@/lib/calls";
import { CallIcon, CallStatus, isAnsweredStatus, customerNumber } from "@/components/call-bits";
import { IvrCallFlow } from "@/components/ivr-call-flow";
import { buildIvrTree, buildIvrTreeFromNodes } from "@/lib/ivr-flow";
import { ArrowLeft, ChevronDown, Download } from "lucide-react";

export const Route = createFileRoute("/_app/calls/$id")({
  head: () => ({ meta: [{ title: "Call details — EESWEB" }] }),
  component: CallDetail,
});

type CallDoc = {
  id: string; clientId: string; smartflo_call_id: string; uuid: string | null;
  direction: string; status: string; from_number: string | null; to_number: string | null;
  did_number: string | null; agent_name: string | null; agent_number: string | null;
  duration_sec: number | null; billable_sec: number | null; wait_sec: number | null;
  hangup_cause: string | null; started_at: string | null; answered_at: string | null;
  ended_at: string | null; recording_url: string | null; recording_available: boolean | null;
  ivr_path: string | null; raw: unknown;
};

function CallDetail() {
  const { id } = Route.useParams();
  const { role } = useAuth();

  const { data: call, isLoading, error } = useQuery({
    queryKey: ["call", id],
    queryFn: async () => {
      // Rules deny another tenant's call — surface that as "not found".
      try {
        const snap = await getDoc(doc(firestore(), "calls", id));
        return snap.exists() ? ({ ...(snap.data() as Omit<CallDoc, "id">), id: snap.id } as CallDoc) : null;
      } catch {
        return null;
      }
    },
  });
  // Watch just this call (one read per change) so a live call updates in place.
  const qc = useQueryClient();
  const canWatch = call !== undefined && call !== null;
  useEffect(() => {
    if (!canWatch) return;
    let first = true;
    return onSnapshot(doc(firestore(), "calls", id), (snap) => {
      if (first) { first = false; return; }
      qc.setQueryData(["call", id], snap.exists() ? ({ ...(snap.data() as Omit<CallDoc, "id">), id: snap.id } as CallDoc) : null);
      qc.invalidateQueries({ queryKey: ["call-events", id] });
    }, () => {});
  }, [id, canWatch, qc]);
  const { data: events } = useQuery({
    queryKey: ["call-events", id, call?.clientId],
    enabled: !!call,
    queryFn: async () => {
      // clientId filter lets the rules engine prove tenant ownership for client users.
      const rows = await queryDocs<{ event_type: string; occurred_at: string; payload?: Record<string, unknown> }>("call_events", { call_id: id, clientId: call!.clientId });
      return rows.sort((a, b) => String(a.occurred_at).localeCompare(String(b.occurred_at)));
    },
  });

  const { data: ivrNodes } = useQuery({
    queryKey: ["call-ivr-nodes", call?.clientId],
    enabled: !!call?.clientId,
    queryFn: () => queryDocs<{ id: string; parent_id: string | null; digit: string; key_path: string; label: string; is_active: boolean; phone_number: string | null; sort_order?: number }>("ivr_menu_nodes", { clientId: call!.clientId }),
  });
  const { data: ivrRoutes } = useQuery({
    queryKey: ["call-ivr-routes", call?.clientId],
    enabled: !!call?.clientId && !!ivrNodes && ivrNodes.length === 0,
    queryFn: () => queryDocs<{ key_path: string; label: string }>("ivr_routes", { clientId: call!.clientId }),
  });

  const flowTree = useMemo(
    () => (ivrNodes && ivrNodes.length > 0
      ? buildIvrTreeFromNodes([...ivrNodes].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)))
      : buildIvrTree(ivrRoutes ?? [])),
    [ivrNodes, ivrRoutes],
  );
  const audioUrl = recordingUrl(call);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-4xl space-y-4" aria-busy="true">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-40 w-full rounded-2xl" />
        <Skeleton className="h-28 w-full rounded-2xl" />
      </div>
    );
  }
  if (!call || error) {
    return (
      <div className="mx-auto max-w-4xl space-y-4">
        <BackLink />
        <div className="rounded-2xl border border-dashed px-6 py-14 text-center">
          <p className="font-medium">Call not found</p>
          <p className="mt-1 text-sm text-muted-foreground">It may belong to another dealership, or the link is out of date.</p>
        </div>
      </div>
    );
  }

  const live = isReallyLive(call);
  // Talk time only exists once the call has ended.
  const answered = isAnsweredStatus(call.status) && !live;
  const timeline = buildTimeline(call, events);
  const dirLabel = call.direction === "outbound" ? "Outgoing call" : call.direction === "inbound" ? "Incoming call" : "Call";

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <BackLink />

      <section className="rounded-2xl border bg-card shadow-soft">
        <div className="flex flex-wrap items-start justify-between gap-3 p-5 md:p-6">
          <div className="flex min-w-0 items-start gap-3">
            <span className="mt-0.5 grid size-10 shrink-0 place-items-center rounded-full bg-muted">
              <CallIcon direction={call.direction} status={call.status} className="size-[18px]" />
            </span>
            <div className="min-w-0">
              <h1 className="truncate font-display text-2xl font-semibold tracking-tight tabular">{customerNumber(call) ?? "Unknown number"}</h1>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {dirLabel} · {formatWhen(call.started_at)}
                {call.direction === "outbound" ? (call.from_number && <> · from <span className="tabular">{formatPhone(call.from_number)}</span></>) : ((call.did_number ?? call.to_number) && <> · to <span className="tabular">{formatDid(call.did_number ?? call.to_number)}</span></>)}
              </p>
            </div>
          </div>
          <CallStatus status={call.status} live={live} className="px-2.5 py-1 text-sm" />
        </div>
        <dl className="grid grid-cols-2 border-t sm:grid-cols-4 [&>div:nth-child(2n)]:border-l sm:[&>div:not(:first-child)]:border-l [&>div:nth-child(n+3)]:border-t sm:[&>div:nth-child(n+3)]:border-t-0">
          <Fact label="Talk time" value={answered ? formatDuration(call.billable_sec) : "—"} />
          <Fact label="Answered by" value={call.agent_name ?? (answered ? "Not recorded" : "No one")} />
          <Fact label="Waited" value={formatDuration(call.wait_sec)} />
          <Fact label="Total length" value={live ? "Still on the line" : formatDuration(call.duration_sec)} />
        </dl>
      </section>

      {(call.recording_available || audioUrl) && (
        <section className="rounded-2xl border bg-card p-5 shadow-soft">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold">Recording</h2>
            {audioUrl && (
              <Button variant="ghost" size="sm" asChild>
                <a href={audioUrl} download={`call-${call.smartflo_call_id}.mp3`}><Download className="size-4" /> Download</a>
              </Button>
            )}
          </div>
          {audioUrl
            ? <audio controls preload="metadata" src={audioUrl} className="w-full" />
            : <p className="text-sm text-muted-foreground">Smartflo hasn't made the recording available yet. Check back in a few minutes.</p>}
        </section>
      )}

      {/* IVR call flow — collapsible tree, auto-opens with the route the caller travelled highlighted. */}
      {flowTree.length > 0 && (
        <IvrCallFlow nodes={flowTree} activePath={call.ivr_path} title="Path through the IVR menu" className="rounded-2xl shadow-soft" />
      )}

      <section className="rounded-2xl border bg-card p-5 shadow-soft">
        <h2 className="mb-4 text-sm font-semibold">What happened</h2>
        {timeline.length === 0 ? (
          <p className="text-sm text-muted-foreground">Smartflo hasn't reported any steps for this call yet.</p>
        ) : (
          <ol className="relative space-y-4 pl-5 before:absolute before:bottom-1.5 before:left-[5px] before:top-1.5 before:w-px before:bg-border">
            {timeline.map((e, i) => (
              <li key={i} className="relative">
                <span className="absolute -left-5 top-1.5 size-[11px] rounded-full border-2 border-card bg-primary" />
                <div className="flex flex-wrap items-baseline gap-x-3">
                  <span className="text-sm font-medium">{e.label}</span>
                  <span className="text-xs text-muted-foreground tabular">
                    {e.at ? new Date(e.at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", second: "2-digit" }) : ""}
                  </span>
                </div>
              </li>
            ))}
          </ol>
        )}
        {timeline.length > 0 && (!events || events.length === 0) && (
          <p className="mt-4 text-xs text-muted-foreground">From Smartflo's call record.</p>
        )}
      </section>

      <details className="group rounded-2xl border bg-card shadow-soft">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 rounded-2xl px-5 py-4 text-sm font-semibold hover:bg-muted/40 [&::-webkit-details-marker]:hidden">
          Technical details
          <ChevronDown className="size-4 text-muted-foreground transition-transform group-open:rotate-180" />
        </summary>
        <div className="space-y-4 border-t px-5 py-4">
          <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
            <Field label="Dialled number (DID)" value={call.did_number ?? "—"} />
            <Field label="Agent extension" value={call.agent_number ?? "—"} />
            <Field label="Answered at" value={formatDateTime(call.answered_at)} />
            <Field label="Ended at" value={formatDateTime(call.ended_at)} />
            <Field label="Hang-up cause" value={call.hangup_cause ?? "—"} />
            <Field label="Smartflo call ID" value={call.smartflo_call_id} mono />
            <Field label="UUID" value={call.uuid ?? "—"} mono />
          </dl>
          {role === "admin" && (
            <div>
              <div className="mb-1.5 text-xs font-medium text-muted-foreground">Raw Smartflo payload (admins only)</div>
              <pre className="mono max-h-96 overflow-auto rounded-lg bg-muted/50 p-3 text-xs">{JSON.stringify(call.raw, null, 2)}</pre>
            </div>
          )}
        </div>
      </details>
    </div>
  );
}

function BackLink() {
  return (
    <Link to="/calls" aria-label="Back to call logs" className="inline-flex items-center gap-1 rounded-md text-sm text-muted-foreground hover:text-foreground">
      <ArrowLeft className="size-4" /> Call logs
    </Link>
  );
}

function humanEvent(t: string | null | undefined): string {
  if (!t) return "Event";
  const s = t.replace(/[_.]/g, " ").trim().toLowerCase();
  if (s.includes("received on server")) return "Call came in";
  if (s.includes("auto attendant")) return "Welcome message played";
  if (s.includes("dtmf")) return "Caller pressed a key";
  if (s.includes("answered")) return "Answered";
  if (s.includes("hangup")) return "Call ended";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

type Step = { label: string; at: string | null };
/**
 * Step-by-step story of the call. Webhook events when Smartflo sent them;
 * otherwise rebuilt from the call record (calls imported from Smartflo's
 * call history have start / answer / end times but no events).
 */
function buildTimeline(call: { direction: string; status: string; started_at: string | null; answered_at: string | null; ended_at: string | null; agent_name: string | null; ivr_path: string | null }, events: { event_type: string | null; occurred_at: string | null; payload?: Record<string, unknown> }[] | undefined): Step[] {
  if (events && events.length > 0) {
    const steps = events.map((e) => {
      const p = e.payload ?? {};
      // Smartflo often sends these as "" — treat blank as missing.
      const key = p.dtmf_input || p.$dtmf_input || p.digit;
      const who = p.agent_name || p.$agent_name || call.agent_name;
      let label = humanEvent(e.event_type);
      // Smartflo's per-menu "cdr" event: the caller reached this IVR menu.
      if (String(e.event_type).toLowerCase() === "cdr") label = p.ivr_name ? `Menu: ${p.ivr_name}` : "In the IVR menu";
      if (label === "Caller pressed a key" && key) label = `Caller pressed ${key}`;
      // Start, answer and end use the call's own times (from Smartflo's call
      // record), not when the webhook happened to reach us a few seconds later.
      let at = e.occurred_at;
      if (label === "Call came in") at = call.started_at ?? at;
      if (label === "Answered") {
        if (call.answered_at) at = call.answered_at;
        else if (!isAnsweredStatus(call.status)) return null; // the IVR picking up, not a person
        if (who) label = `Answered by ${who}`;
      }
      if (label === "Call ended") {
        at = call.ended_at ?? at;
        if (!call.answered_at && !isAnsweredStatus(call.status)) label = `Ended without an answer (${callStatusLabel(call.status).toLowerCase()})`;
      }
      return { label, at };
    }).filter((s): s is Step => s !== null);
    // Time order; on a tie the call's arrival comes first.
    const first = (s: Step) => (s.label === "Call came in" || s.label === "Call placed" ? 0 : 1);
    return steps.sort((a, b) => String(a.at ?? "").localeCompare(String(b.at ?? "")) || first(a) - first(b));
  }
  const steps: Step[] = [];
  if (call.started_at) steps.push({ label: call.direction === "outbound" ? "Call placed" : "Call came in", at: call.started_at });
  if (call.ivr_path) steps.push({ label: `Caller pressed ${call.ivr_path.replace(/>/g, " then ")}`, at: null });
  if (call.answered_at) steps.push({ label: call.agent_name ? `Answered by ${call.agent_name}` : "Answered", at: call.answered_at });
  if (call.ended_at) steps.push({ label: call.answered_at ? "Call ended" : `Ended without an answer (${callStatusLabel(call.status).toLowerCase()})`, at: call.ended_at });
  return steps;
}

function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0 px-5 py-3.5 md:px-6">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 truncate font-medium tabular">{value}</dd>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={mono ? "mono mt-0.5 truncate text-xs" : "mt-0.5 truncate"}>{value}</dd>
    </div>
  );
}
